import React, { useEffect, useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Badge,
  Button,
  Card,
  CardBody,
  Col,
  Collapse,
  Input,
  Row,
  Spinner,
  Table,
} from 'reactstrap';
import ReactDrawer from 'react-drawer';
import 'react-drawer/lib/react-drawer.css';
import moment from 'moment';
import {
  fetchNotificationLogList,
  fetchNotificationLogFilterOptions,
} from '../action/action';
import DHPagination from '../../../../components/Pagination';
import WhatsAppPreview from './WhatsAppPreview.component';

const STATUS_OPTIONS = [
  'assigned',
  'confirmed',
  'delivery_scheduled',
  'rescheduled',
  'start_trip',
  'you_are_next',
  'delivered',
  'delivery_success',
  'partially_delivered',
  'slot_released',
  'delivery_fail',
  'canceled',
  'address_changed',
];

const formatJson = (value) => {
  if (!value) return '';
  // request_data/response_data are MySQL JSON columns — the driver already
  // returns parsed objects, but tolerate a raw string too just in case.
  if (typeof value !== 'string') {
    return JSON.stringify(value, null, 2);
  }
  try {
    return JSON.stringify(JSON.parse(value), null, 2);
  } catch (e) {
    return value;
  }
};

const NotificationLogList = ({ dateFrom, dateTo }) => {
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [limit, setLimit] = useState(10);

  const [soNumberDraft, setSoNumberDraft] = useState('');
  const [countryDraft, setCountryDraft] = useState('');
  const [triggedStatusDraft, setTriggedStatusDraft] = useState('');
  const [notificationStatusDraft, setNotificationStatusDraft] = useState('');
  const [dispatchLocationDraft, setDispatchLocationDraft] = useState('');
  const [dispatchLocations, setDispatchLocations] = useState([]);
  const [filters, setFilters] = useState({
    soNumber: '',
    country: '',
    triggedStatus: '',
    notificationStatus: '',
    dispatchLocation: '',
  });

  const [open, setOpen] = useState(false);
  const [selectedLog, setSelectedLog] = useState(null);
  const [showRawData, setShowRawData] = useState(false);

  const setPaginationValue = ({ page: p }) => setPage(Number(p));
  const setLimitValue = ({ limit: l }) => {
    setLimit(Number(l));
    setPage(0);
  };

  const getLogList = useCallback(
    async (params) => {
      try {
        setLoading(true);
        await fetchNotificationLogList({ params, dispatch });
      } finally {
        setLoading(false);
      }
    },
    [dispatch],
  );

  useEffect(() => {
    getLogList({
      pagination: { page: page + 1, limit },
      filter: {
        soNumber: filters.soNumber || undefined,
        country: filters.country || undefined,
        triggedStatus: filters.triggedStatus || undefined,
        notificationStatus: filters.notificationStatus || undefined,
        dispatchLocation: filters.dispatchLocation || undefined,
        dateFrom,
        dateTo,
      },
    });
  }, [page, limit, filters, dateFrom, dateTo, getLogList]);

  /* Loaded once - the options are the distinct values already logged, so they
     do not change with the other filters. */
  useEffect(() => {
    const loadDispatchLocations = async () => {
      const response = await fetchNotificationLogFilterOptions({ dispatch });
      setDispatchLocations(response?.dispatchLocations || []);
    };
    loadDispatchLocations();
  }, [dispatch]);

  const handleSearch = () => {
    setPage(0);
    setFilters({
      soNumber: soNumberDraft,
      country: countryDraft,
      triggedStatus: triggedStatusDraft,
      notificationStatus: notificationStatusDraft,
      dispatchLocation: dispatchLocationDraft,
    });
  };

  const handleReset = () => {
    setSoNumberDraft('');
    setCountryDraft('');
    setTriggedStatusDraft('');
    setNotificationStatusDraft('');
    setDispatchLocationDraft('');
    setPage(0);
    setFilters({
      soNumber: '',
      country: '',
      triggedStatus: '',
      notificationStatus: '',
      dispatchLocation: '',
    });
  };

  const { logList } = useSelector((state) => state.notificationManager);
  const items = logList?.items || [];
  const totalPages = logList?.pageInfo?.totalPages || 0;

  const viewDetails = (log) => {
    setSelectedLog(log);
    setShowRawData(false);
    setOpen(true);
  };

  return (
    <Card className="mt-0" style={{ borderTopLeftRadius: 0 }}>
      <CardBody>
        <Row className="mb-3 g-2 align-items-center">
          <Col md={2}>
            <Input
              placeholder="Search SO Number"
              value={soNumberDraft}
              onChange={(e) => setSoNumberDraft(e.target.value)}
            />
          </Col>
          <Col md={2}>
            <Input
              placeholder="Country"
              value={countryDraft}
              onChange={(e) => setCountryDraft(e.target.value)}
            />
          </Col>
          <Col md={2}>
            <Input
              type="select"
              value={dispatchLocationDraft}
              onChange={(e) => setDispatchLocationDraft(e.target.value)}
            >
              <option value="">All Dispatch Locations</option>
              {dispatchLocations.map((location) => (
                <option key={location} value={location}>
                  {location}
                </option>
              ))}
            </Input>
          </Col>
          <Col md={2}>
            <Input
              type="select"
              value={triggedStatusDraft}
              onChange={(e) => setTriggedStatusDraft(e.target.value)}
            >
              <option value="">All Statuses</option>
              {STATUS_OPTIONS.map((status) => (
                <option key={status} value={status}>
                  {status}
                </option>
              ))}
            </Input>
          </Col>
          <Col md={2}>
            <Input
              type="select"
              value={notificationStatusDraft}
              onChange={(e) => setNotificationStatusDraft(e.target.value)}
            >
              <option value="">All (Success/Failed)</option>
              <option value="success">Success</option>
              <option value="failed">Failed</option>
            </Input>
          </Col>
          <Col md={2} className="d-flex gap-2">
            <Button color="secondary" onClick={handleSearch}>
              <i className="bx bx-search-alt align-middle"></i> Search
            </Button>
            <Button outline color="danger" onClick={handleReset}>
              <i className="bx bx-revision align-middle"></i>
            </Button>
          </Col>
        </Row>

        <div className="table-responsive dt-table">
          <Table className="table mb-0">
            <thead>
              <tr>
                <th>SO Number</th>
                <th>Country</th>
                <th nowrap="nowrap">Dispatch Location</th>
                <th>Triggered Status</th>
                <th>Notification Status</th>
                <th>Mobile No</th>
                <th>Customer Name</th>
                <th>Created At</th>
                <th></th>
              </tr>
            </thead>
            {loading ? (
              <tbody>
                <tr>
                  <td colSpan={9} className="text-center py-4">
                    <Spinner color="primary" />
                  </td>
                </tr>
              </tbody>
            ) : items.length > 0 ? (
              <tbody>
                {items.map((log) => (
                  <tr key={log.id}>
                    <td>{log.so_number}</td>
                    <td>{log.country}</td>
                    <td>{log.dispatch_location || '-'}</td>
                    <td>{log.trigged_status}</td>
                    <td>
                      <Badge
                        color={
                          log.notification_status === 'success'
                            ? 'success'
                            : 'danger'
                        }
                      >
                        {log.notification_status}
                      </Badge>
                    </td>
                    <td>{log.mobile_no}</td>
                    <td>{log.customer_name}</td>
                    <td nowrap="nowrap">
                      {moment(log.created_at).format('Do MMM YYYY h:mmA')}
                    </td>
                    <td>
                      <Button
                        outline
                        color="warning"
                        size="sm"
                        onClick={() => viewDetails(log)}
                      >
                        <i className="mdi mdi-eye-outline"></i> View
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            ) : (
              <tbody>
                <tr>
                  <td colSpan={9}>No Records found</td>
                </tr>
              </tbody>
            )}
          </Table>
        </div>

        {items.length > 0 ? (
          <DHPagination
            totalPages={totalPages}
            currentPage={page}
            setPaginationValue={setPaginationValue}
            setLimitValue={setLimitValue}
          />
        ) : null}
      </CardBody>

      <ReactDrawer
        open={open}
        position="right"
        onClose={() => setOpen(false)}
        className="sideNavWidth"
      >
        <Card className="card-h-100" style={{ overflowY: 'scroll' }}>
          <CardBody>
            <div className="d-flex justify-content-between align-items-start">
              <h5>Notification Detail — {selectedLog?.so_number}</h5>
              <button
                type="button"
                className="btn-close"
                onClick={() => setOpen(false)}
                aria-label="Close"
              ></button>
            </div>
            <p className="mb-1">
              <b>Country:</b> {selectedLog?.country}
            </p>
            <p className="mb-1">
              <b>Dispatch Location:</b> {selectedLog?.dispatch_location || '-'}
            </p>
            <p className="mb-1">
              <b>Triggered Status:</b> {selectedLog?.trigged_status}
            </p>
            <p className="mb-3">
              <b>Notification Status:</b> {selectedLog?.notification_status}
            </p>

            <p className="mb-1">
              <b>WhatsApp Message</b>
            </p>
            <div className="mb-3">
              <WhatsAppPreview
                messageText={selectedLog?.message_text}
                buttonLink={selectedLog?.message_link}
                createdAt={selectedLog?.created_at}
                success={selectedLog?.notification_status === 'success'}
              />
            </div>

            <Button
              color="link"
              size="sm"
              className="p-0 mb-2"
              onClick={() => setShowRawData((prev) => !prev)}
            >
              <i className={`mdi mdi-chevron-${showRawData ? 'down' : 'right'} align-middle`}></i>{' '}
              {showRawData ? 'Hide' : 'Show'} raw request/response data
            </Button>
            <Collapse isOpen={showRawData}>
              <p className="mb-1">
                <b>Request Data</b>
              </p>
              <pre style={{ whiteSpace: 'pre-wrap', fontSize: '12px' }}>
                {formatJson(selectedLog?.request_data)}
              </pre>
              <p className="mb-1">
                <b>Response Data</b>
              </p>
              <pre style={{ whiteSpace: 'pre-wrap', fontSize: '12px' }}>
                {formatJson(selectedLog?.response_data)}
              </pre>
            </Collapse>
          </CardBody>
        </Card>
      </ReactDrawer>
    </Card>
  );
};

export default NotificationLogList;
